import React, { useContext, useState } from 'react';

import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite';

import ActivityLogsView from 'components/settings/activity_logs/ActivityLogsView';
import AllProductsContainer from 'components/all_products/AllProductsContainer';
import AppNavBar, { AppNavBarContext } from 'components/layout/AppNavBar';
import { AuthContext } from 'components/providers/AuthContextProvider';
import FsaCopilot from 'components/upb_viewer_beta/copilot/FsaCopilot';
import AssignThumbnailsView from 'components/product_lists/product_list/AssignThumbnailsView';
import AttributeScoringView from 'components/layout/attribute_scoring/AttributeScoringView';
import AttributeSubCategoryScoringView from 'components/layout/attribute_scoring/AttributeSubCategoryScoringView';
import BrandCleanupView from 'components/manage_brands/BrandCleanupView';
import BrandDetailsView from 'components/manage_brands/brand_details/BrandDetailsView';
import BrandsHarvestView from 'components/manage_brands/harvest_brands/BrandsHarvestView';
import BrandStatsDashboardView from 'components/manage_brands/brand_stats_dashboard/BrandStatsDashboardView';
import BrandVerifyView from 'components/manage_brands/brand_verify/BrandVerifyView';
import CutDryImportTracking from 'components/cut_dry_product_import_tracking/CutDryImportTracking';
import CutDryImportTrackingDetails from 'components/cut_dry_product_import_tracking/CutDryImportTrackingDetails';
import ConfigureSchema from 'components/manage_attribute/manage_schema/ConfigureSchema';
import CreateBrandView from 'components/manage_brands/CreateBrandView';
import CreateManufacturerView from 'components/manage_manufacturers/CreateManufacturerView';
import DashboardView from 'components/dashboard/DashboardView';
import DataSourcesDashboardView from 'components/data_sources/dashboard/DataSourcesDashboardView';
import DataSourcesView from 'components/data_sources/DataSourcesView';
import DataStreamsView from 'components/data_streams/DataStreamsView';
import JobsView from 'components/jobs/JobsView';
import FileExportsView from 'components/global_products_exports/FileExportsView';
import FullScreenMainContainer from 'components/layout/FullScreenMainContainer';
import RebateAnalysisView from 'components/rebate_analysis/RebateAnalysisView';
import RebateAnalysisFileView from 'components/rebate_analysis/RebateAnalysisFileView';
import GenerateReportsView from 'components/generate_reports/GenerateReportsView';
import GlobalProductsContainer from 'components/global_products/GlobalProductsContainer';
import GlobalProductsDetailContainer from 'components/global_products/detail_view/GlobalProductsDetailContainer';
import GlobalManufacturerHarvestView from 'components/manage_manufacturers/global_manufacturer_harvest/GlobalManufacturerHarvestView';
import GS1LookUpView from 'components/gs1_lookup/GS1LookUpView';
import GS1XMLDecoder from 'components/tools/GS1XMLDecoder';
import ImageSuggesterView from 'components/enrich/image_suggester/ImageSuggesterView';
import ImageTagger2View from 'components/image_tagger_2/ImageTagger2View.jsx';
import InternalToolsView from 'components/internaltools/InternalToolsView';
import L4DefaultImageView from 'components/l4_default_images/L4DefaultImageView';
import L4SegmentationView from 'components/l4_segmentation/L4SegmentationView';
import ManageAttributeContainer from 'components/manage_attribute/ManageAttributeContainer';
import MarketShareView from 'components/upb_viewer/MarketShareView';
import ManageAttributeValueAlternatives from 'components/manage_attribute_value_maps/ManageAttributeValueAlternatives';
import ManageAttributeValueContainer from 'components/manage_attribute_value/ManageAttributeValueContainer';
import ManageBrandsView from 'components/manage_brands/ManageBrandsView';
import ManageDisplayValueView from 'components/manage_display_values/ManageDisplayValueView';
import BrandContentQAView from 'components/manage_brands/BrandContentQAView';
import BrandQAV2 from 'components/manage_brands/BrandQAV2';
import SimilarBrandsView from 'components/manage_brands/SimilarBrandsView';
import ManageManufacturersView from 'components/manage_manufacturers/ManageManufacturersView';
import ManageProductOutliersView from 'components/product_anomalies/ManageProductOutliersView';
import ManageSchemaContainer from 'components/manage_attribute/manage_schema/ManageSchemaContainer';
import ManageVendorsView from 'components/manage_vendors/ManageVendorsView';
import ManageTaxonomyView from 'components/manage_taxonomy/ManageTaxonomyView';
import ManufacturerDetailsView from 'components/manage_manufacturers/manufacturer_details/ManufacturerDetailsView';
import NutritionalPanelView from 'components/nutritional_panels/NutritionalPanelView';
import PDFQAView from 'components/product_enrichment_resources/PDFQAView';
import PDFProductDetailsView from 'components/product_enrichment_resources/PDFProductDetailsView';
import ProductContentValidatorView from 'components/product_content_validator/ProductContentValidatorView';
import ProductDetailsPage from 'components/product_details/ProductDetailsPage';
import ProductEnrichmentResourcesView from 'components/product_enrichment_resources/ProductEnrichmentResourcesView';
import ProductsToValidateTable from 'components/product_content_validator/ProductsToValidateTable';
import ProductsLinksSuggestionsView from 'components/global_products/global_products_view/ProductsLinksSuggestionsView';
import ProductListExportContainer from 'components/product_lists/product_export/ProductListExportContainer';
import ProductListsView from 'components/product_lists/ProductListsView';
import ProductListView from 'components/product_lists/product_list/ProductListView';
import ProductOutliersFeedbackView from 'components/product_anomalies/QA/ProductOutliersFeedbackView';
import ProductOutliersQAView from 'components/product_anomalies/QA/ProductOutliersQAView';
import ProductsWithASaleIn3MAndUnmappedView from 'components/enrich/ProductsWithASaleIn3MAndUnmappedView';
import ProductUpdateDetails from 'components/product_updates/product_update_details/ProductUpdateDetails';
import ProductUpdatesView from 'components/product_updates/ProductUpdatesView';
import ProtectedRoute from 'components/layout/ProtectedRoute';
import ReviewView from 'components/l4_default_images/review_view/ReviewView';
import SkuMappingWorkflowEnrichView from 'components/enrich/sku_mapping_workflow/SkuMappingWorkflowEnrichView';
import BrandSkuMapperManufacturersView from 'components/brand_sku_mapper/BrandSkuMapperManufacturersView';
import BrandSkuMapperManufacturerView from 'components/brand_sku_mapper/BrandSkuMapperManufacturerView';
import BrandSkuMapperRunReviewView from 'components/brand_sku_mapper/BrandSkuMapperRunReviewView';
import BrandSkuMapperV2ReviewView from 'components/brand_sku_mapper_v2/BrandSkuMapperV2ReviewView';
import SingleDataSourceView from 'components/data_sources/single_data_source_view/SingleDataSourceView';
import SingleDataStreamViewContainer from 'components/data_streams/SingleDataStreamViewContainer';
import SupplierBrandManagementView from 'components/supplier_brand_management/SupplierBrandManagementView';
import AwsBrandSyncView from 'components/aws_brand_sync/AwsBrandSyncView';
import SupportTicketsView from 'components/settings/support_tickets/SupportTicketsView';
import SyncDataView from 'components/sync_data/SyncDataView';
import TasksView from 'components/settings/team/tasks/TasksView';
import TeamView from 'components/settings/team/TeamView';
import UnstructuredTabView from 'components/enrich/unstructured_view/UnstructuredTabView';
import UPBProductDetailsPage from 'components/upb_viewer/UPBProductDetailsPage';
import UPBViewContainer from 'components/upb_viewer/UPBViewContainer';
import UPBViewerBetaContainer from 'components/upb_viewer_beta/UPBViewerBetaContainer';
import UPBMatchExplorerContainer from 'components/upb_match_explorer/UPBMatchExplorerContainer';
import MfrViewerContainer from 'components/upb_mfr_viewer/MfrViewerContainer';
import UploadedNutritionalPanelView from 'components/nutritional_panels/UploadedNutritionalPanelView';
import UserPermission from 'lib/enums/UserPermission';
import UserTimeLogView from 'components/settings/user_time_logs/UserTimeLogView';
import VendorDetailsView from 'components/manage_vendors/vendor_details/VendorDetailsView';

function AppContainer() {
  const { homeUrl } = useContext(AuthContext);
  const [appNavBarConfig, setAppNavBarConfig] = useState({});
  const [menuBarVisible, setMenuBarVisible] = useState(true);

  return (
    <AppNavBarContext.Provider value={setAppNavBarConfig}>
      <Router>
        <div className={css(styles.pageWrapper)}>
          <div className={css(styles.main)}>
            <AppNavBar
              expanded={menuBarVisible}
              setExpanded={setMenuBarVisible}
              {...appNavBarConfig}
            />
            <FullScreenMainContainer expanded={menuBarVisible}>
              <Switch>
                <ProtectedRoute
                  path="/sync-data"
                  exact={true}
                  component={<SyncDataView />}
                  permissions={[UserPermission.DATA_SYNC]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/generate-reports/:type"
                  exact={true}
                  component={<GenerateReportsView />}
                  permissions={[UserPermission.DATA_SYNC]} //??
                  fallbackUrl={homeUrl}
                />
                <Redirect from="/deviated-price-analysis" to="/rebate-analysis" exact />
                <Redirect
                  from="/deviated-price-analysis/:upload_id/view"
                  to="/rebate-analysis/:upload_id/view"
                  exact
                />
                <ProtectedRoute
                  path="/rebate-analysis"
                  exact={true}
                  component={<RebateAnalysisView />}
                  permissions={[UserPermission.ADMIN_OPERATIONS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/rebate-analysis/:upload_id/view"
                  exact={true}
                  component={<RebateAnalysisFileView />}
                  permissions={[UserPermission.ADMIN_OPERATIONS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/task-view"
                  exact={true}
                  component={<TasksView />}
                  permissions={[UserPermission.TASKS_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-updates/details"
                  component={<ProductUpdateDetails />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-updates"
                  exact={true}
                  component={<ProductUpdatesView />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/global-product-linkages/qa-product-changes"
                  exact={true}
                  component={<ProductOutliersQAView />}
                  permissions={[UserPermission.VIEW_SKU_MAPPINGS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/global-product-linkages/qa-product-feedback"
                  exact={true}
                  component={<ProductOutliersFeedbackView />}
                  permissions={[UserPermission.VIEW_SKU_MAPPINGS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/global-product-linkages"
                  exact={true}
                  component={<ManageProductOutliersView />}
                  permissions={[UserPermission.VIEW_SKU_MAPPINGS]}
                  fallbackUrl={homeUrl}
                />
                <Route path="/file-exports">
                  <FileExportsView />
                </Route>
                <Route path="/global-products*">
                  <GlobalProductsContainer />
                </Route>
                <ProtectedRoute
                  path="/data-sources/:dataSourceId/product-export"
                  exact={true}
                  component={<ProductListExportContainer />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <Route path="/data-sources" exact={true}>
                  <DataSourcesView />
                </Route>
                <ProtectedRoute
                  path="/data-sources-dashboard"
                  exact={true}
                  component={<DataSourcesDashboardView />}
                  permissions={[UserPermission.MANAGE_DATA_SOURCES]}
                  fallbackUrl={homeUrl}
                />
                <Route path="/data-sources/:record_id">
                  <SingleDataSourceView />
                </Route>
                <Route path="/data-streams" exact={true}>
                  <DataStreamsView />
                </Route>
                <Route path="/data-streams/:record_id">
                  <SingleDataStreamViewContainer />
                </Route>
                <ProtectedRoute
                  path="/jobs"
                  exact={true}
                  component={<JobsView />}
                  permissions={[UserPermission.VIEW_JOBS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/cut-dry-import-tracking/:runId"
                  exact={true}
                  component={<CutDryImportTrackingDetails />}
                  permissions={[UserPermission.MANAGE_DATA_SOURCES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/cut-dry-import-tracking"
                  exact={true}
                  component={<CutDryImportTracking />}
                  permissions={[UserPermission.MANAGE_DATA_SOURCES]}
                  fallbackUrl={homeUrl}
                />
                <Route path="/all-products">
                  <AllProductsContainer />
                </Route>
                <ProtectedRoute
                  path="/assign-attributes"
                  exact={true}
                  component={<ManageAttributeContainer />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-attributes"
                  exact={true}
                  component={<ManageAttributeValueContainer />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-attribute-value-alternatives"
                  exact={true}
                  component={<ManageAttributeValueAlternatives />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-schema/:schemaId"
                  component={<ConfigureSchema />}
                  permissions={[UserPermission.MANAGE_PRODUCT_LISTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-schema"
                  exact={true}
                  component={<ManageSchemaContainer />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-attributes/:attribute_name/:attribute_type/:attribute_id"
                  exact={true}
                  component={<ManageAttributeValueContainer />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/l4-segmentation"
                  component={<L4SegmentationView />}
                  permissions={[UserPermission.MANAGE_TAXONOMY]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-taxonomy"
                  component={<ManageTaxonomyView manageTaxonomy={true} />}
                  permissions={[UserPermission.MANAGE_TAXONOMY]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/attribute-scoring"
                  exact={true}
                  component={<AttributeScoringView />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/attribute-scoring/sub-category"
                  exact={true}
                  component={<AttributeSubCategoryScoringView />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-content-validator"
                  exact={true}
                  component={<ProductContentValidatorView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-content-validator/data-source/:data_source_id"
                  exact={true}
                  component={<ProductsToValidateTable />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manage-brands/brand-content-qa"
                  component={<BrandContentQAView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/vendor-products-links-suggestions"
                  component={<ProductsLinksSuggestionsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-qa"
                  component={<BrandQAV2 />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-verify"
                  component={<BrandVerifyView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-cleanup"
                  component={<BrandCleanupView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manage-brands/similar-brand/:similarBrandName?"
                  component={<SimilarBrandsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manage-brands/brand-stats"
                  component={<BrandStatsDashboardView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-harvest"
                  component={<BrandsHarvestView />}
                  permissions={[UserPermission.BRAND_HARVESTING]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manage-brands"
                  component={<ManageBrandsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-details/:brandID?"
                  component={<BrandDetailsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/create-brand/:brandID?"
                  component={<CreateBrandView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manage-manufacturers"
                  component={<ManageManufacturersView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/create-manufacturer"
                  component={<CreateManufacturerView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manufacturer-details/:manufacturerID?"
                  component={<ManufacturerDetailsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/manage-vendors"
                  component={<ManageVendorsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/global-manufacturer-harvest"
                  component={<GlobalManufacturerHarvestView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/vendor-details/:vendorID?"
                  component={<VendorDetailsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/l4-default-images/review"
                  component={<ReviewView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/l4-default-images/"
                  component={<L4DefaultImageView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/image-suggester"
                  component={<ImageSuggesterView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/gs1-lookup/"
                  component={<GS1LookUpView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/sku-mapping-workflow/"
                  component={<SkuMappingWorkflowEnrichView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                {/* Brand SKU Mapper routes use ENRICH_PRODUCTS, consistent with the
                        rest of the Enrich section. All ops users who perform SKU-mapping
                        work already hold this permission. A dedicated EDIT_SKU_MAPPINGS
                        permission should be introduced when finer-grained access control
                        is required. */}
                {/* New run-less product views */}
                <ProtectedRoute
                  path="/enrich-products/brand-sku-mapper/all-gmv-products"
                  component={<BrandSkuMapperRunReviewView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-sku-mapper/manufacturers/:mfrId/products"
                  component={<BrandSkuMapperRunReviewView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-sku-mapper/manufacturers/:manufacturerId"
                  component={<BrandSkuMapperManufacturerView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-sku-mapper-v2"
                  component={<BrandSkuMapperV2ReviewView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/brand-sku-mapper"
                  component={<BrandSkuMapperManufacturersView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/products-with-a-sale-in-3m-and-unmapped/"
                  component={<ProductsWithASaleIn3MAndUnmappedView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/products-with-a-sale-in-3m-and-unstructured/"
                  component={<UnstructuredTabView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/settings/team"
                  component={<TeamView />}
                  permissions={[UserPermission.VIEW_USER_MANAGEMENT]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/time-logs/:Usertype?"
                  component={<UserTimeLogView />}
                  permissions={[]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/settings/activity-logs"
                  component={<ActivityLogsView />}
                  permissions={[UserPermission.MANAGE_ACTIVITY_LOGS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/support-tickets/:ticketId?"
                  component={<SupportTicketsView />}
                  permissions={[UserPermission.MANAGE_SUPPORT_TICKETS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-lists/:productListId/product-list-export"
                  exact={true}
                  component={<ProductListExportContainer />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-lists/:productListId"
                  component={<ProductListView />}
                  permissions={[UserPermission.MANAGE_PRODUCT_LISTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-lists"
                  component={<ProductListsView />}
                  permissions={[UserPermission.MANAGE_PRODUCT_LISTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/assign-thumbnails/:ProductListId?/:ProductListName?"
                  component={<AssignThumbnailsView />}
                  permissions={[UserPermission.MANAGE_PRODUCT_LISTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/upb-viewer/:l1_category?/:l2_category?/:l3_category?/:l4_category?"
                  component={<UPBViewContainer />}
                  permissions={[UserPermission.UPB_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/upb-sales-viewer/:l1_category?/:l2_category?/:l3_category?/:l4_category?"
                  component={<UPBViewContainer />}
                  permissions={[UserPermission.UPB_SALES_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/upb-mfr-viewer-beta/:manufacturer_id?/:brand_id?"
                  component={<MfrViewerContainer navExpanded={menuBarVisible} />}
                  permissions={[UserPermission.UPB_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/upb-mfr-viewer"
                  component={<UPBViewContainer />}
                  permissions={[UserPermission.UPB_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/upb-viewer-beta/:l1_category?/:l2_category?/:l3_category?/:l4_category?"
                  component={<UPBViewerBetaContainer navExpanded={menuBarVisible} />}
                  permissions={[UserPermission.UPB_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path={[
                    '/upb-match-explorer/band/:bandIndex/dp/:dpId',
                    '/upb-match-explorer/band/:bandIndex',
                    '/upb-match-explorer/dp/:dpId',
                    '/upb-match-explorer',
                  ]}
                  component={<UPBMatchExplorerContainer />}
                  permissions={[UserPermission.UPB_VIEW_ADMIN]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/market-share/:productId"
                  component={<MarketShareView />}
                  permissions={[UserPermission.UPB_VIEW]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/image-tagger2"
                  component={<ImageTagger2View />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/upb-pdp/:productId/:productType/:viewGMV?"
                  component={<UPBProductDetailsPage />}
                  permissions={[UserPermission.MANAGE_PRODUCT_LISTS, UserPermission.UPB_SALES_VIEW]}
                  fallbackUrl={homeUrl}
                  hasAll={false}
                />
                <ProtectedRoute
                  path="/product-details/:productId/:productType"
                  component={<ProductDetailsPage />}
                  permissions={[UserPermission.MANAGE_PRODUCT_LISTS, UserPermission.UPB_SALES_VIEW]}
                  fallbackUrl={homeUrl}
                  hasAll={false}
                />
                <ProtectedRoute
                  path="/global-product-detail/:globalProductId/:productID?/:productType?"
                  component={<GlobalProductsDetailContainer />}
                  permissions={[UserPermission.EDIT_PRODUCT]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/manage-display-values"
                  component={<ManageDisplayValueView />}
                  permissions={[UserPermission.EDIT_ATTRIBUTES]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/internaltools"
                  exact={true}
                  component={<InternalToolsView />}
                  permissions={[UserPermission.ADMIN_OPERATIONS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/dashboard"
                  component={<DashboardView />}
                  permissions={[UserPermission.VIEW_DASHBOARD]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/nutritional-panel-uploads/:nutritionalPanelUploadID?"
                  component={<UploadedNutritionalPanelView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/nutritional-panel-upload"
                  component={<NutritionalPanelView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-enrichment-resources/pdf-details/:resourceId/:pdfUrlId"
                  component={<PDFProductDetailsView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-enrichment-resources/pdf-qa/:resourceId"
                  component={<PDFQAView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/product-enrichment-resources"
                  component={<ProductEnrichmentResourcesView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/tools/gs1-decoder"
                  exact={true}
                  component={<GS1XMLDecoder />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/supplier-brand-management"
                  exact={true}
                  component={<SupplierBrandManagementView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <ProtectedRoute
                  path="/enrich-products/aws-brand-sync"
                  exact={true}
                  component={<AwsBrandSyncView />}
                  permissions={[UserPermission.ENRICH_PRODUCTS]}
                  fallbackUrl={homeUrl}
                />
                <Route>
                  <Redirect to={homeUrl} />
                </Route>
              </Switch>
            </FullScreenMainContainer>
          </div>
          {/* Marco — global docked assistant, available across the FSA app (like machan) */}
          <FsaCopilot />
        </div>
      </Router>
    </AppNavBarContext.Provider>
  );
}

const styles = StyleSheet.create({
  main: {
    transition: 'margin .5s',
    width: '100%',
  },
  pageWrapper: {
    height: '100%',
    overflowX: 'none',
    width: '100%',
    minHeight: '90vh',
  },
});

export default AppContainer;
